import FileDownload from 'js-file-download';

import client from '../api/client';
import { OpenClassCertificateEmailResult } from '../openClasses/types/certificateEmail';
import {
  AttendeeSignatureType,
  CertificateDateFormat,
  CertificateLanguage,
  CertificateTemplate,
  CertificateTemplateType,
  SignatureType,
} from './types/Certificate';
import { ImageMedia } from '../types/ImageMedia';

const getCertificateForAttendee = (
  fileName: string,
  attendeeId: number,
  languageId: number,
) =>
  client
    .get(`/certificates/attendees/${attendeeId}/languages/${languageId}`, {
      responseType: 'blob',
    })
    .then((response) => FileDownload(response.data, `${fileName}.pdf`));

const getCertificateForElsaParticipant = (
  fileName: string,
  elsaParticipantId: string,
  courseInstanceId: number,
  languageId: number,
) =>
  client
    .get(
      `/certificates/elsa-participants/${elsaParticipantId}/languages/${languageId}`,
      {
        responseType: 'blob',
        params: { courseInstanceId },
      },
    )
    .then((response) => FileDownload(response.data, `${fileName}.pdf`));

const getCertificateZip = (
  fileName: string,
  courseId: number,
  courseInstanceId: number,
  languageId: number,
  attendeeIds?: number[],
  attendeeElsaIds?: string[],
) =>
  client
    .get(`/certificates/courses/${courseId}/languages/${languageId}`, {
      responseType: 'blob',
      params: { courseInstanceId, attendeeIds, attendeeElsaIds },
    })
    .then((response) => FileDownload(response.data, `${fileName}.zip`));

const sendCertificateEmails = (
  courseInstanceId: number,
  languageId: number,
  attendeeIds?: number[],
  attendeeElsaIds?: string[],
) =>
  client.post(`/certificates/languages/${languageId}/send-email`, {
    courseInstanceId,
    languageId,
    attendeeIds,
    attendeeElsaIds,
  });

const getCertificatesLanguages = (name?: string, code?: string) =>
  client.get<CertificateLanguage[]>(`/certificates/languages/`, {
    params: {
      name,
      code,
    },
  });

export type CertificateLanguageParams = Omit<CertificateLanguage, 'id'>;

const createCertificateLanguage = (data: CertificateLanguageParams) =>
  client.post<CertificateLanguage>('/certificates/languages', data);

const updateCertificateLanguage = (
  id: number,
  data: CertificateLanguageParams,
) => client.patch<CertificateLanguage>(`/certificates/languages/${id}`, data);

const deleteCertificateLanguage = (id: number) =>
  client.delete(`/certificates/languages/${id}`);

const getCertificateImages = (name?: string, code?: string) =>
  client.get<ImageMedia[]>(`/certificates/images/`, {
    params: {
      name,
      code,
    },
  });

const createCertificateImage = (data: FormData) =>
  client.post<ImageMedia>(`/certificates/images`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

const updateCertificateImage = (id: number, data: FormData) =>
  client.patch<ImageMedia>(`/certificates/images/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

const deleteCertificateImage = (id: number) =>
  client.delete(`/certificates/images/${id}`);

interface CreateLanguageTextContent {
  title: string;
  description: string;
  languageId: number;
  emailSubject: string;
  emailBody: string;
  promotionalLink1?: string;
  promotionalLink2?: string;
  promotionalLinkButtonText1?: string;
  promotionalLinkButtonText2?: string;
}

type CreateCertificateTemplateParams = {
  courseId: number;
  type: CertificateTemplateType;
  languageTextContents: CreateLanguageTextContent[];
  instructorSignatureType: SignatureType;
  attendeeSignatureType: AttendeeSignatureType;
  issuerSignatureType: SignatureType;
  issuerId: number | null;
  validForMonths: number | null;
  dateFormat: CertificateDateFormat;
  stampImage1?: number | null;
  stampImage2?: number | null;
  footerText1?: string;
  footerText2?: string;
};

type UpdateCertificateTemplateParams = {
  instructorSignatureType: SignatureType;
  attendeeSignatureType: AttendeeSignatureType;
  issuerSignatureType: SignatureType;
  issuerId: number | null;
  validForMonths: number | null;
  dateFormat: CertificateDateFormat;
  languageTextContents: CreateLanguageTextContent[];
  stampImage1?: number | null;
  stampImage2?: number | null;
  footerText1?: string;
  footerText2?: string;
};

const createCertificateTemplate = (
  templateParams: CreateCertificateTemplateParams,
) =>
  client.post<CertificateTemplate>(`/certificates/templates/`, templateParams);

const updateCertificateTemplate = (
  id: number,
  templateParams: UpdateCertificateTemplateParams,
) =>
  client.patch<CertificateTemplate>(
    `/certificates/templates/${id}`,
    templateParams,
  );

const getCourseCertificateTemplates = (courseId: number) =>
  client.get<CertificateTemplate[]>(
    `/certificates/templates/courses/${courseId}`,
  );

const deleteCertificateTemplate = (id: number) =>
  client.delete(`/certificates/templates/${id}`);

const getCertificatePreview = (
  courseId: number,
  languageId: number,
  fileName: string,
  params: {
    attendeeName: string;
    location: string;
    classDate: string;
    instructorId: number;
  },
) =>
  client
    .get(`/certificates/courses/${courseId}/languages/${languageId}/preview`, {
      responseType: 'blob',
      params: params,
    })
    .then((response) => {
      FileDownload(response.data, `${fileName}.pdf`);
      return response.status;
    });

// Separate from the standard preview since furbo is a fixed dutch document with no language
// to pick, and needs birth details the standard preview has no field for. Those go in the body
// so they stay out of browser history and access logs.
const getFurboCertificatePreview = (
  courseId: number,
  fileName: string,
  params: {
    attendeeName: string;
    birthDate: string;
    birthPlace: string;
    startDate: string;
    endDate: string;
  },
) =>
  client
    .post(`/certificates/courses/${courseId}/furbo-preview`, params, {
      responseType: 'blob',
    })
    .then((response) => {
      FileDownload(response.data, `${fileName}.pdf`);
      return response.status;
    });

const getOpenClassCertificateZip = (
  fileName: string,
  openClassId: number,
  registrationIds: number[],
  certificateTemplateId: number,
  languageId?: number,
) =>
  client
    .get(`/certificates/open-classes/${openClassId}`, {
      params: { certificateTemplateId, languageId, registrationIds },
      responseType: 'blob',
    })
    .then((response) => FileDownload(response.data, `${fileName}.zip`));

// One request for the whole selection, the backend refuses it as a whole or emails it as a whole
const emailOpenClassCertificates = (
  openClassId: number,
  registrationIds: number[],
  certificateTemplateId: number,
  languageId?: number,
  resend = false,
) =>
  client
    .post<OpenClassCertificateEmailResult[]>(
      `/certificates/open-classes/${openClassId}/email`,
      {
        certificateTemplateId,
        languageId,
        registrationIds,
        resend,
      },
    )
    .then(({ data }) => data);

export default {
  getCertificateForAttendee,
  getCertificateForElsaParticipant,
  getCertificateZip,
  emailOpenClassCertificates,
  getOpenClassCertificateZip,
  getCertificatesLanguages,
  createCertificateLanguage,
  updateCertificateLanguage,
  deleteCertificateLanguage,
  createCertificateTemplate,
  getCourseCertificateTemplates,
  deleteCertificateTemplate,
  updateCertificateTemplate,
  createCertificateImage,
  updateCertificateImage,
  getCertificateImages,
  deleteCertificateImage,
  getCertificatePreview,
  getFurboCertificatePreview,
  sendCertificateEmails,
};
